import { Decimal } from 'decimal.js';
import { flow, isEmpty, isNil, replace, includes } from 'lodash';
import { DEFAULT_THOUSANDS_DIVIDER } from '@config/global-constants';

type DecimalValue = Decimal.Value | DecimalNumber | { toString: () => string };
type DecimalOperateMethod<T = Decimal> = (decimalNum: DecimalValue) => T;

/**
 * DecimalNumber class, we should use this to instead of Number.
 * known issue:
 * can't be cloned by structuredClone(window.history.pushState will error too):
 * due to decimal.js issue: https://github.com/MikeMcl/decimal.js/issues/224
 * if u want to clone this, u must use number(valueOf)
 */
class DecimalNumber {
  public get decimalValue(): Decimal {
    return this._decimalValue;
  }

  public get valueStr(): string {
    return this.toString();
  }

  public get isNaN(): boolean {
    return this._decimalValue.isNaN();
  }

  public get isFinite(): boolean {
    return this._decimalValue.isFinite();
  }

  protected _decimalValue: Decimal;

  /**
   * u can use this to judge is the decimal number valid
   *
   * @param decimalNum decimal number
   */
  public static isValidDecimal(decimalNum: DecimalValue): boolean {
    try {
      DecimalNumber.buildDecimalValue(decimalNum);

      return true;
    } catch (error) {
      return false;
    }
  }

  public static buildDecimalValue(
    decimalNum: DecimalValue,
    defaultValue?: DecimalValue,
  ): Decimal {
    const withoutThousandSepDecimal = includes(
      decimalNum?.toString(),
      DEFAULT_THOUSANDS_DIVIDER,
    )
      ? replace(
          decimalNum?.toString(),
          new RegExp(DEFAULT_THOUSANDS_DIVIDER, 'g'),
          '',
        )
      : decimalNum;

    return isNaN(Number(withoutThousandSepDecimal))
      ? new Decimal(NaN)
      : new Decimal(
          DecimalNumber.getSafetyDecimalNum(
            withoutThousandSepDecimal,
            defaultValue,
          )?.toString(),
        );
  }

  public static getSafetyDecimalNum(
    decimalNum: DecimalValue,
    defaultValue?: DecimalValue,
  ): DecimalValue {
    const isDefaultValueSafety = !DecimalNumber.isDecimalInValid(defaultValue);

    return DecimalNumber.isDecimalInValid(decimalNum) && isDefaultValueSafety
      ? defaultValue
      : decimalNum;
  }

  public static isDecimalInValid(decimalNum: DecimalValue): boolean {
    return (
      isNaN(Number(decimalNum)) ||
      isNil(decimalNum) ||
      isEmpty(decimalNum?.toString())
    );
  }

  public setDecimalValue(
    decimalNum: DecimalValue,
    defaultValue: DecimalValue,
  ): void {
    this._decimalValue = DecimalNumber.buildDecimalValue(
      decimalNum,
      defaultValue,
    );
  }

  /**
   * use this to calc number avoid lost your number precision
   *
   * @param decimalNum null will use defaultValue, undefined or string like '23sss' will use NaN
   * @param defaultValue default value
   */
  public constructor(decimalNum: DecimalValue, defaultValue: DecimalValue = 0) {
    this.setDecimalValue(decimalNum, defaultValue);
  }

  /**
   * flow to operate decimal operation
   * now will
   * [
   * 1. buildDecimalValue to build a safety decimal number with default value
   * 2. operateMethod with bind to this._value
   * ]
   *
   * @param operateMethod operation method you want to do
   * @param defaultValue default value when decimal number is undefined / null / ''
   */
  protected flowedDecimalOperate<T>(
    operateMethod: DecimalOperateMethod<T>,
    defaultValue?: DecimalValue,
  ): DecimalOperateMethod<T> {
    return flow(
      (decimalNum) => DecimalNumber.buildDecimalValue(decimalNum, defaultValue),
      operateMethod.bind(this._decimalValue),
    );
  }

  /**
   * add
   *
   * @param decimalNum right number
   */
  public add(
    decimalNum: DecimalValue,
    defaultValue: DecimalValue = 0,
  ): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(
      this._decimalValue.add,
      defaultValue,
    )(decimalNum);

    return new DecimalNumber(calcDecimal);
  }

  /**
   * subtraction
   *
   * @param decimalNum right number
   */
  public sub(
    decimalNum: DecimalValue,
    defaultValue: DecimalValue = 0,
  ): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(
      this._decimalValue.sub,
      defaultValue,
    )(decimalNum);

    return new DecimalNumber(calcDecimal);
  }

  /**
   * multiply
   *
   * @param decimalNum right number
   */
  public mul(
    decimalNum: DecimalValue,
    defaultValue: DecimalValue = 1,
  ): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(
      this._decimalValue.mul,
      defaultValue,
    )(decimalNum);

    return new DecimalNumber(calcDecimal);
  }

  /**
   * division
   *
   * @param decimalNum right number
   */
  public div(
    decimalNum: DecimalValue,
    defaultValue: DecimalValue = 1,
  ): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(
      this._decimalValue.div,
      defaultValue,
    )(decimalNum);

    return new DecimalNumber(calcDecimal);
  }

  public divToInt(
    decimalNum: DecimalValue,
    defaultValue: DecimalValue = 1,
  ): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(
      this._decimalValue.divToInt,
      defaultValue,
    )(decimalNum);

    return new DecimalNumber(calcDecimal);
  }

  public isInt(): boolean {
    return this._decimalValue.isInt();
  }

  public mod(decimalNum: DecimalValue): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(this._decimalValue.mod)(
      decimalNum,
    );

    return new DecimalNumber(calcDecimal);
  }

  public pow(powNum: DecimalValue): DecimalNumber {
    const calcDecimal = this.flowedDecimalOperate(this._decimalValue.pow)(
      powNum,
    );

    return new DecimalNumber(calcDecimal);
  }

  public ceil(): DecimalNumber {
    return new DecimalNumber(this._decimalValue.ceil());
  }

  public floor(): DecimalNumber {
    return new DecimalNumber(this._decimalValue.floor());
  }

  public lt(decimalNum: DecimalValue): boolean {
    return this.flowedDecimalOperate<boolean>(this._decimalValue.lt)(
      decimalNum,
    );
  }

  public lte(decimalNum: DecimalValue): boolean {
    return this.flowedDecimalOperate<boolean>(this._decimalValue.lte)(
      decimalNum,
    );
  }

  public eq(decimalNum: DecimalValue): boolean {
    return this.flowedDecimalOperate<boolean>(this._decimalValue.eq)(
      decimalNum,
    );
  }

  public gt(decimalNum: DecimalValue): boolean {
    return this.flowedDecimalOperate<boolean>(this._decimalValue.gt)(
      decimalNum,
    );
  }

  public gte(decimalNum: DecimalValue): boolean {
    return this.flowedDecimalOperate<boolean>(this._decimalValue.gte)(
      decimalNum,
    );
  }

  public comparedTo(decimalNum: DecimalValue): number {
    return this.flowedDecimalOperate<number>(this._decimalValue.cmp)(
      decimalNum,
    );
  }

  public toFixed(decimalPlaces?: number): string {
    return this._decimalValue.toFixed(
      parseFloat(decimalPlaces?.toString()) || 0,
    );
  }

  public toString(): string {
    return this._decimalValue.toString();
  }

  public toJSON(): number {
    return this.valueOf();
  }

  public valueOf(): number {
    return Number(this.toString());
  }

  public clone(): DecimalNumber {
    return new DecimalNumber(this.toString());
  }

  public abs(): DecimalNumber {
    return new DecimalNumber(this._decimalValue.abs());
  }

  /**
   * override this to make sure Object.getPrototypeOf returns [object Number]
   */
  public get [Symbol.toStringTag](): string {
    return 'Number';
  }
}

/**
 * you can use this to create DecimalNumber without new
 *
 * @param decimalNum null will use defaultValue, undefined or string like '23sss' will use NaN
 * @param defaultValue default value
 */
const decimalNumber = (
  decimalNum: DecimalValue,
  defaultValue: DecimalValue = 0,
): DecimalNumber => new DecimalNumber(decimalNum, defaultValue);

export { DecimalValue, DecimalOperateMethod, DecimalNumber, decimalNumber };
